import React, { useState } from 'react';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ListPage from '../../../sharedModules/listPage/ListPage';
import { SelectOption } from '../../../sharedModules/form/model';
import { useGet } from '../../../sharedModules/hooks/useQuery';
import InspectionReportMetricStrip from '../components/InspectionReportMetricStrip';

const formatCompletedAt = (value: string | null | undefined): string => {
  if (value == null) return '';
  try {
    return format(new Date(value), 'MM/dd/yyyy');
  } catch {
    return value;
  }
};

// Coarse relative time — an admin reads "6d ago", never a timestamp. Precision is
// bounded by the 60s activity heartbeat anyway, so anything finer would be false.
const formatRelative = (value: string | null | undefined): string => {
  if (value == null) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return value;

  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const SECTION_LABELS: Record<string, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  water_treatment: 'Water',
  air_quality: 'Air Quality',
  customer_information: 'Customer Info',
};

const formatSections = (sections: string[] | null | undefined): string =>
  (sections ?? []).map((key) => SECTION_LABELS[key] ?? key).join(', ');

const formatMedia = (row: { photo_count?: number; video_count?: number }): string => {
  const parts: string[] = [];
  if (row.photo_count) parts.push(`${row.photo_count} photo${row.photo_count === 1 ? '' : 's'}`);
  if (row.video_count) parts.push(`${row.video_count} video${row.video_count === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
};

const reportColumns = [
  { name: 'Name', selector: 'name', sortable: true, grow: 1 },
  { name: 'Technician', selector: 'technician', sortable: true, grow: 1 },
  { name: 'Order #', selector: 'order_no', sortable: true, grow: 0.75 },
  {
    name: 'Date',
    selector: 'date',
    sortable: true,
    grow: 0.75,
    format: (row: { date?: string | null }) => formatCompletedAt(row.date),
  },
  {
    name: 'Completed At',
    selector: 'completed_at',
    sortable: true,
    grow: 0.75,
    format: (row: { completed_at?: string | null }) => formatCompletedAt(row.completed_at),
  },
];

const abandonedColumns = [
  { name: 'Name', selector: 'name', sortable: true, grow: 1 },
  { name: 'Opened By', selector: 'opened_by', sortable: false, grow: 1 },
  { name: 'Order #', selector: 'order_no', sortable: true, grow: 0.75 },
  { name: 'Opened', selector: 'opened_count', sortable: true, grow: 0.5 },
  {
    name: 'Last Activity',
    selector: 'last_activity_at',
    sortable: true,
    grow: 0.75,
    format: (row: { last_activity_at?: string | null }) => formatRelative(row.last_activity_at),
  },
  {
    name: 'Sections Touched',
    selector: 'sections_touched',
    sortable: false,
    grow: 1.25,
    format: (row: { sections_touched?: string[] }) => formatSections(row.sections_touched),
  },
  {
    name: 'Media',
    selector: 'photo_count',
    sortable: true,
    grow: 0.75,
    format: (row: { photo_count?: number; video_count?: number }) => formatMedia(row),
  },
];

const clearFilterSetting = {
  home_inspection_job_scheduled_date_gteq: '',
  home_inspection_job_scheduled_date_lteq: '',
  home_inspection_job_customer_name_i_cont: '',
  home_inspection_job_order_no_i_cont: '',
  submitted_by_emp_id_i_cont: '',
};

// Same filter shape, but the technician column on an activity row is emp_id rather
// than submitted_by_emp_id.
const abandonedClearFilterSetting = {
  home_inspection_job_scheduled_date_gteq: '',
  home_inspection_job_scheduled_date_lteq: '',
  home_inspection_job_customer_name_i_cont: '',
  home_inspection_job_order_no_i_cont: '',
  emp_id_i_cont: '',
};

const InspectionReportListPage: React.FC = () => {
  const [tab, setTab] = useState(0);

  const { data: technicianOptions } = useGet<{ technician_options: SelectOption[] }>({
    url: '/home_services/inspection_jobs/new',
  });

  const technicianChoices = technicianOptions?.data?.technician_options || [];

  const reportFilters = [
    { label: 'Technician', type: 'select', param: 'submitted_by_emp_id_i_cont', choices: technicianChoices, width: 2 },
    { label: 'Date', type: 'datePicker', param: 'home_inspection_job_scheduled_date', width: 5 },
  ];

  const abandonedFilters = [
    { label: 'Technician', type: 'select', param: 'emp_id_i_cont', choices: technicianChoices, width: 2 },
    { label: 'Date', type: 'datePicker', param: 'home_inspection_job_scheduled_date', width: 5 },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <InspectionReportMetricStrip />

        <Tabs value={tab} onChange={(_e, next) => setTab(next)} sx={{ mb: 2 }}>
          <Tab label="Completed" />
          <Tab label="Abandoned" />
        </Tabs>

        {tab === 0 && (
          <ListPage
            clickableRows
            clearedFilterSetting={clearFilterSetting}
            columns={reportColumns}
            controller="home_services/inspection_reports"
            defaultSort="created_at desc"
            defaultFilters={{}}
            filterDefinitions={reportFilters}
            includeFilterBar
            queryPlaceholder="Search by Customer or Order #"
            title="Home Services"
            subtitle="Completed Reports"
            rowClickPath="home_services/reports"
          />
        )}

        {tab === 1 && (
          <ListPage
            clearedFilterSetting={abandonedClearFilterSetting}
            columns={abandonedColumns}
            controller="home_services/inspection_report_activities"
            defaultSort="last_activity_at desc"
            defaultFilters={{}}
            filterDefinitions={abandonedFilters}
            includeFilterBar
            queryPlaceholder="Search by Customer or Order #"
            title="Home Services"
            subtitle="Opened but not submitted"
          />
        )}
      </Grid>
    </Grid>
  );
};

export default InspectionReportListPage;
